/**
 * ProcessThread Form Component
 * Form for creating and editing process threads
 */

import React, { useState } from 'react';
import { ProcessThread, CreateProcessThreadRequest, UpdateProcessThreadRequest } from '@flow-studio/api';
import { processThreadApiClient } from '@flow-studio/api';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { useAuthStore } from '@flow-studio/store'; // import { authStore';
import { FormGroup, FormLabel, FormActions, FormError, Input, Textarea, Checkbox, Button } from '../Shared/FormUI';

interface ProcessThreadFormProps {
  processThread?: ProcessThread;
  processId: number;
  onSuccess: (processThread: ProcessThread) => void;
  onCancel: () => void;
}

export const ProcessThreadForm: React.FC<ProcessThreadFormProps> = ({
  processThread,
  processId,
  onSuccess,
  onCancel,
}) => {
  const { showToast } = useUIStore();
  const { getTenantId: _getTenantId } = useAuthStore();
  const isEdit = !!processThread;

  const [formData, setFormData] = useState({
    Name: processThread?.Name || '',
    Description: processThread?.Description || '',
    // New threads default to inactive — CK_Thread_HasVersion requires CurrentVersionID when IsActive=true,
    // which only exists after the first canvas save. Editing an existing thread preserves its value.
    IsActive: processThread?.IsActive !== undefined ? processThread.IsActive : false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (isEdit) {
        // Update existing process thread - include ProcessID and TenantID from the existing entity
        const updateRequest: UpdateProcessThreadRequest = {
          ProcessThreadID: processThread!.ProcessThreadID,
          ...formData,
          ProcessID: processThread!.ProcessID, // Include ProcessID from server data
          TenantID: processThread!.TenantID, // Include TenantID from server data
        };
        response = await processThreadApiClient.update(updateRequest);
      } else {
        const createRequest: CreateProcessThreadRequest = {
          ...formData,
          ProcessID: processId,
        };
        response = await processThreadApiClient.create(createRequest);
      }

      if (response.success && response.data) {
        showToast('success', isEdit ? 'Workflow updated successfully!' : 'Workflow created successfully!');
        onSuccess(response.data);
      } else {
        const errorMessage = response.error?.message || 'Failed to save workflow';
        setError(errorMessage);
        showToast('error', errorMessage);
      }
    } catch (err) {
      const errorMessage = 'An error occurred while saving the workflow';
      setError(errorMessage);
      showToast('error', errorMessage);
      console.error('Error saving workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <FormError>{error}</FormError>}

      <FormGroup>
        <FormLabel required>Name</FormLabel>
        <Input type="text" value={formData.Name}
          onChange={(e) => handleChange('Name', e.target.value)}
          required placeholder="Enter thread name" />
      </FormGroup>

      <FormGroup>
        <FormLabel>Description</FormLabel>
        <Textarea value={formData.Description}
          onChange={(e) => handleChange('Description', e.target.value)}
          placeholder="Thread description" />
      </FormGroup>

      <FormGroup>
        <Checkbox id="isActive" label="Active"
          checked={formData.IsActive}
          onChange={(e) => handleChange('IsActive', e.target.checked)} />
      </FormGroup>

      <FormActions>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="primary" loading={loading} loadingText="Saving...">
          {isEdit ? 'Update Thread' : 'Create Thread'}
        </Button>
      </FormActions>
    </form>
  );
};